import { isImmutable, Map, Set } from 'immutable';

import { getBarcodeError, getCAPError, getCPIError, getGTINError, getPriceError, getQuantityError, getRetailPriceError, getSKUError, getWholesalePriceError } from '@/utils/validations/inventory';
import { getCategoryError, getConditionError, getIsSupplyError, getProductionPartnersError, getWhenMadeError, getWhoMadeError } from '@/utils/validations/details';
import { getHeightError, getLengthError, getProcessingProfileError, getWeightError, getWidthError, validateShipping } from '@/utils/validations/shipping';
import { isOpenSizingSellingMethodAllowed, isUploadFilesSupported } from '@/utils/product/checkAttribute';
import { clearErrors, reduceErrors, removeErrors, setErrors } from '@/utils/errors';
import { getPhotosError, getPhotosOptimizationErrors } from '@/utils/validations/photos';
import { updateVariations, variantHasPhotos } from '@/utils/variations/variants';
import { validateTaxonomyAttributes } from '@/utils/validations/taxonomyAttributes';
import { validatePersonalization } from '@/utils/validations/personalization';
import { validateProductDetails } from '@/utils/validations/productDetails';
import { updateVariationsErrors } from '@/utils/validations/variations';
import { validateItemSpecifics } from '@/utils/validations/itemSpecifics';
import { getDescriptionError } from '@/utils/validations/description';
import { getMaterialsError } from '@/utils/validations/materials';
import { updateInfluence } from '@/utils/variations/influence';
import { getPropertyType } from '@/utils/taxonomy/ebay/properties';
import { getVideosError } from '@/utils/validations/videos';
import { getTitleError } from '@/utils/validations/title';
import { validateFiles } from '@/utils/validations/files';
import { setIsComplete } from '@/utils/product/isComplete';
import { getTagsError } from '@/utils/validations/tags';
import { makeValue } from '@/utils/product/shapeAttributes';
import { getSize } from '@/utils/iterable/getSize';
import { get } from '@/utils/iterable/get';

import { AMOUNT, CURRENCIES, CURRENCY, ERROR, OPTIMIZATION, OVERSET, PERSONALIZATION_ERRORS, REQUIRED, SELLING_METHOD, SHIPPING_ERRORS, VALUE } from '@/constants/product';
import { ERRORS as VARIATIONS_ERRORS, INFLUENCE, VARIANTS, RETAIN } from '@/constants/variations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ERRORS as PRODUCT_ERRORS } from '@/constants/validations';
import { EMPTY, VELA } from '@/constants';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { STATUS } from '@/constants/listings';
import { FORM } from '@/constants/forms';
import { DATA } from '@/data';

export function shouldValidate({ currency, product, shop, type }) {
  switch (product.get('channel')) {
    case EBAY: {
      if (product.get('status') !== STATUS[VELA].TEMPLATE) return true;

      switch (type) {
        case ATTRIBUTE.ITEM_CATEGORY: {
          const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
          return !!category && category !== AMOUNT.ZERO;
        }

        case ATTRIBUTE.CONDITION: {
          const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
          const condition = product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION]);
          return (
            !!category &&
            category !== AMOUNT.ZERO && (
              condition.get(VALUE) !== null ||
              getSize(condition.get(ATTRIBUTE.DESCRIPTION)) > 0
            )
          );
        }

        case ATTRIBUTE.DESCRIPTION:
        case ATTRIBUTE.PHOTOS:
        case ATTRIBUTE.VIDEOS:
        case ATTRIBUTE.TITLE: {
          return getSize(product.getIn([type, VALUE])) > 0;
        }

        case ATTRIBUTE.ITEM_SPECIFICS: {
          const channel = product.get('channel');
          const marketplaceId = shop.get('marketplaceId');
          const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
          const itemSpecificsValue = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE]);
          return !getCategoryError({ category, channel, marketplaceId }) && getSize(itemSpecificsValue) > 0;
        }

        case ATTRIBUTE.PRODUCT_DETAILS: {
          const channel = product.get('channel');
          const marketplaceId = shop.get('marketplaceId');
          const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
          const productDetailsValue = product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE]);
          return !getCategoryError({ category, channel, marketplaceId }) && getSize(productDetailsValue) > 0;
        }

        case ATTRIBUTE.QUANTITY:
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.SKU: {
          return getSize(product.getIn([FORM.INVENTORY, type, VALUE])) > 0;
        }

        case FORM.SHIPPING: {
          return (
            getSize(product.getIn([FORM.SHIPPING, ATTRIBUTE.LOCATION, VALUE])) > 0 ||
            getSize(product.getIn([FORM.SHIPPING, ATTRIBUTE.POSTAL_CODE, VALUE])) > 0 ||
            !!product.getIn([FORM.SHIPPING, ATTRIBUTE.WEIGHT_MAJOR, VALUE]) ||
            !!product.getIn([FORM.SHIPPING, ATTRIBUTE.WEIGHT_MINOR, VALUE]) ||
            !!product.getIn([FORM.SHIPPING, ATTRIBUTE.DEPTH, VALUE]) ||
            !!product.getIn([FORM.SHIPPING, ATTRIBUTE.LENGTH, VALUE]) ||
            !!product.getIn([FORM.SHIPPING, ATTRIBUTE.WIDTH, VALUE])
          );
        }

        default: {
          return true;
        }
      }
    }

    case ETSY: {
      if (product.get('status') !== STATUS[VELA].TEMPLATE) return true;

      switch (type) {
        case ATTRIBUTE.CATEGORY: {
          return (
            product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]) !== AMOUNT.ZERO ||
            getSize(product.getIn([FORM.VARIATIONS, VARIANTS])) > 0
          );
        }

        case ATTRIBUTE.DESCRIPTION:
        case ATTRIBUTE.FILES:
        case ATTRIBUTE.PHOTOS:
        case ATTRIBUTE.VIDEOS:
        case ATTRIBUTE.TITLE: {
          return getSize(product.getIn([type, VALUE])) > 0;
        }

        case ATTRIBUTE.IS_SUPPLY:
        case ATTRIBUTE.WHEN_MADE:
        case ATTRIBUTE.WHO_MADE: {
          return product.getIn([FORM.DETAILS, type, VALUE]) !== null;
        }

        case ATTRIBUTE.PRODUCTION_PARTNERS: {
          return getSize(product.getIn([FORM.DETAILS, type, VALUE])) > 0;
        }

        case ATTRIBUTE.QUANTITY:
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.SKU: {
          return getSize(product.getIn([FORM.INVENTORY, type, VALUE])) > 0;
        }

        case ATTRIBUTE.RETURN_POLICY: {
          return false;
        }

        case ATTRIBUTE.TAXONOMY_ATTRIBUTES: {
          return !getCategoryError({
            category: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]),
            channel: product.get('channel'),
          });
        }

        case FORM.SHIPPING: {
          return !!product.getIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE]);
        }

        default: {
          return true;
        }
      }
    }

    case FAIRE: {
      switch (product.get('status')) {
        case STATUS[FAIRE].DRAFT:
        case STATUS[VELA].COPIED:
        case STATUS[VELA].STAGING:
        case STATUS[VELA].IMPORTED:
        case STATUS[VELA].UNPUBLISHED: {
          switch (type) {
            case ATTRIBUTE.PHOTOS: {
              return getSize(product.getIn([type, VALUE])) > 0;
            }

            case ATTRIBUTE.RETAIL_PRICE:
            case ATTRIBUTE.WHOLESALE_PRICE: {
              switch (currency) {
                case CURRENCY.USD: {
                  return true;
                }

                default: {
                  return (
                    getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, currency])) > 0 ||
                    getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency])) > 0
                  );
                }
              }
            }

            default: {
              return true;
            }
          }
        }

        case STATUS[VELA].TEMPLATE: {
          switch (type) {
            case ATTRIBUTE.DESCRIPTION:
            case ATTRIBUTE.PHOTOS:
            case ATTRIBUTE.TITLE: {
              return getSize(product.getIn([type, VALUE])) > 0;
            }

            case ATTRIBUTE.GTIN:
            case ATTRIBUTE.HEIGHT:
            case ATTRIBUTE.LENGTH:
            case ATTRIBUTE.WEIGHT:
            case ATTRIBUTE.WIDTH: {
              return getSize(product.getIn([FORM.INVENTORY, type, VALUE])) > 0;
            }

            case ATTRIBUTE.RETAIL_PRICE:
            case ATTRIBUTE.WHOLESALE_PRICE: {
              return (
                getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, currency])) > 0 ||
                getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency])) > 0
              );
            }

            case FORM.SHIPPING: {
              switch (product.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE])) {
                case SELLING_METHOD.CASE: {
                  return (
                    getSize(product.getIn([FORM.SHIPPING, ATTRIBUTE.CASE_SIZE, VALUE])) > 0 ||
                    getSize(product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE])) > 0
                  );
                }

                case SELLING_METHOD.ITEM: {
                  return getSize(product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE])) > 0;
                }

                case SELLING_METHOD.SIZE: {
                  return getSize(product.getIn([FORM.SHIPPING, ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE])) > 0;
                }

                default: {
                  return false;
                }
              }

            }

            default: {
              return true;
            }
          }
        }

        default: {
          switch (type) {
            case ATTRIBUTE.RETAIL_PRICE:
            case ATTRIBUTE.WHOLESALE_PRICE: {
              switch (currency) {
                case CURRENCY.USD: {
                  return true;
                }

                default: {
                  return (
                    getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, currency])) > 0 ||
                    getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency])) > 0
                  );
                }
              }
            }

            default: {
              return true;
            }
          }
        }
      }
    }

    case SHOPIFY: {
      if (product.get('status') !== STATUS[VELA].TEMPLATE) return true;

      switch (type) {
        case ATTRIBUTE.BARCODE:
        case ATTRIBUTE.CAP:
        case ATTRIBUTE.CPI:
        case ATTRIBUTE.QUANTITY:
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.SKU: {
          return getSize(product.getIn([FORM.INVENTORY, type, VALUE])) > 0;
        }

        case ATTRIBUTE.DESCRIPTION:
        case ATTRIBUTE.PHOTOS:
        case ATTRIBUTE.TITLE:
        case ATTRIBUTE.VIDEOS: {
          return getSize(product.getIn([type, VALUE])) > 0;
        }

        case ATTRIBUTE.PAGE_TITLE:
        case ATTRIBUTE.META_DESCRIPTION: {
          return getSize(product.getIn([FORM.SEO, type, VALUE])) > 0;
        }

        case ATTRIBUTE.WEIGHT: {
          return (
            getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE])) === true &&
            getSize(product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, VALUE])) > 0
          );
        }

        default: {
          return true;
        }
      }
    }

    default: {
      return true;
    }
  }
}

function updateError({ oversetType, path, product, type, validator }) {
  function getPath(keys) {
    return path
      ? typeof path === 'string'
        ? [path, ...keys]
        : Array.isArray(path)
          ? [...path, ...keys]
          : keys
      : keys;
  }

  if (oversetType && product.getIn(getPath([oversetType, OVERSET]))) {
    return product.getIn(getPath([type, ERROR])) || product.getIn([ERROR, type])
      ? clearErrors({ errors: [type], path, product })
      : product;
  }

  const error = validator(product.getIn(getPath([type, VALUE])));

  return error
    ? setErrors({ errors: Map({ [type]: error }), path, product })
    : product.getIn(getPath([type, ERROR])) || product.getIn([ERROR, type])
      ? clearErrors({ errors: [type], path, product })
      : product;
}

function updateErrors({ errorsTypes, path, product, validator }) {
  const errors = validator(product);

  return getSize(errors)
    ? setErrors({ errors, path, product })
    : clearErrors({ errors: errorsTypes, path, product });
}

function updateOptimizationErrors({ attribute, product }) {
  switch (attribute) {
    case ATTRIBUTE.PHOTOS: {
      const channel = product.get('channel');
      const photos = product.getIn([FORM.PHOTOS, VALUE]);
      return product.setIn([FORM.PHOTOS, OPTIMIZATION], getPhotosOptimizationErrors({ channel, photos }));
    }

    default: {
      return product;
    }
  }
}

export function setPhotosErrors({ product }) {
  const channel = product.get('channel');

  switch (channel) {
    case EBAY: {
      if (!product.hasIn(['score', ATTRIBUTE.PHOTOS])) {
        return updateError({
          product,
          type: ATTRIBUTE.PHOTOS,
          validator(photos) {
            if (!shouldValidate({ product, type: ATTRIBUTE.PHOTOS })) return false;

            function hasPhotos(variant) {
              return variantHasPhotos({ channel, variant });
            }

            if (getSize(photos) === 0 && product.getIn([FORM.VARIATIONS, VARIANTS]).some(hasPhotos)) {
              return false;
            }

            return getPhotosError({ channel, photos });
          },
        });
      }

      return updateOptimizationErrors({
        attribute: ATTRIBUTE.PHOTOS,
        product: updateError({
          product,
          type: ATTRIBUTE.PHOTOS,
          validator(photos) {
            if (!shouldValidate({ product, type: ATTRIBUTE.PHOTOS })) return false;

            function hasPhotos(variant) {
              return variantHasPhotos({ channel, variant });
            }

            if (getSize(photos) === 0 && product.getIn([FORM.VARIATIONS, VARIANTS]).some(hasPhotos)) {
              return false;
            }

            return getPhotosError({ channel, photos });
          },
        }),
      });
    }

    default: {
      if (!product.hasIn(['score', ATTRIBUTE.PHOTOS])) {
        return updateError({
          product,
          type: ATTRIBUTE.PHOTOS,
          validator(photos) {
            return (
              shouldValidate({ product, type: ATTRIBUTE.PHOTOS }) &&
              getPhotosError({ channel, photos })
            );
          },
        });
      }

      return updateOptimizationErrors({
        attribute: ATTRIBUTE.PHOTOS,
        product: updateError({
          product,
          type: ATTRIBUTE.PHOTOS,
          validator(photos) {
            return (
              shouldValidate({ product, type: ATTRIBUTE.PHOTOS }) &&
              getPhotosError({ channel, photos })
            );
          },
        }),
      });
    }
  }
}

export function setVideosError({ product }) {
  return updateError({
    product,
    type: ATTRIBUTE.VIDEOS,
    validator(videos) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.VIDEOS }) &&
        getVideosError({ channel: product.get('channel'), videos })
      );
    },
  });
}

export function setTitleErrors({ product }) {
  return updateError({
    product,
    type: ATTRIBUTE.TITLE,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.TITLE }) &&
        getTitleError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setDescriptionErrors({ product }) {
  return updateError({
    product,
    type: ATTRIBUTE.DESCRIPTION,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.DESCRIPTION }) &&
        getDescriptionError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setWhoMadeErrors({ product }) {
  return updateError({
    path: FORM.DETAILS,
    product,
    type: ATTRIBUTE.WHO_MADE,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.WHO_MADE }) &&
        getWhoMadeError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setIsSupplyErrors({ product }) {
  return updateError({
    path: FORM.DETAILS,
    product,
    type: ATTRIBUTE.IS_SUPPLY,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.IS_SUPPLY }) &&
        getIsSupplyError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setItemSpecificsErrors({ product, shop, shopData }) {
  return updateErrors({
    errorsTypes: [ATTRIBUTE.ITEM_SPECIFICS],
    path: FORM.DETAILS,
    product,
    validator() {
      const marketplaceId = shop.get('marketplaceId');
      return shouldValidate({ product, shop, type: ATTRIBUTE.ITEM_SPECIFICS })
        ? validateItemSpecifics({ marketplaceId, product, shopData })
        : EMPTY.MAP;
    },
  });
}

export function setProductDetailsErrors({ product, shop }) {
  return updateErrors({
    errorsTypes: [ATTRIBUTE.PRODUCT_DETAILS],
    path: FORM.DETAILS,
    product,
    validator() {
      const marketplaceId = shop.get('marketplaceId');
      return shouldValidate({ product, shop, type: ATTRIBUTE.PRODUCT_DETAILS })
        ? validateProductDetails({ marketplaceId, product })
        : EMPTY.MAP;
    },
  });
}

export function setWhenMadeErrors({ product }) {
  return updateError({
    path: FORM.DETAILS,
    product,
    type: ATTRIBUTE.WHEN_MADE,
    validator(value) {
      return shouldValidate({ product, type: ATTRIBUTE.WHEN_MADE }) && (
        getWhenMadeError({ channel: product.get('channel'), value }) || (
          product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]) &&
          !isUploadFilesSupported({ product }) &&
          !!getSize(product.getIn([FORM.FILES, VALUE])) &&
          PRODUCT_ERRORS.DETAILS.DIGITAL_MADE_TO_ORDER
        )
      );
    },
  });
}

export function setCategoryErrors({ product, shop }) {
  const channel = product.get('channel');

  switch (channel) {
    case EBAY: {
      return updateError({
        path: FORM.DETAILS,
        product,
        type: ATTRIBUTE.ITEM_CATEGORY,
        validator(category) {
          const marketplaceId = shop.get('marketplaceId');
          return (
            shouldValidate({ product, type: ATTRIBUTE.ITEM_CATEGORY }) && (
              getCategoryError({ category, channel: EBAY, marketplaceId }) || (
                getSize(product.getIn([FORM.VARIATIONS, VARIANTS])) > 0 &&
                get([EBAY, marketplaceId, 'CATEGORIES', category, 'features', 'variations'])(DATA) === false &&
                PRODUCT_ERRORS.VARIATIONS.NOT_ALLOWED
              )
            )
          );
        },
      });
    }

    default: {
      return updateError({
        path: FORM.DETAILS,
        product,
        type: ATTRIBUTE.CATEGORY,
        validator(category) {
          return (
            shouldValidate({ product, type: ATTRIBUTE.CATEGORY }) &&
            getCategoryError({ category, channel })
          );
        },
      });
    }
  }
}

export function setConditionErrors({ product, shop }) {
  return updateError({
    path: FORM.DETAILS,
    product,
    type: ATTRIBUTE.CONDITION,
    validator() {
      const channel = product.get('channel');
      const marketplaceId = shop.get('marketplaceId');
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
      const condition = product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION]);
      return (
        shouldValidate({ product, type: ATTRIBUTE.CONDITION }) &&
        !getCategoryError({ category, channel, marketplaceId }) &&
        getConditionError({ category, channel, condition, marketplaceId })
      );
    },
  });
}

export function setFilesErrors({ product }) {
  return updateErrors({
    errorsTypes: [ATTRIBUTE.FILES],
    product,
    validator(source) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.FILES }) &&
        validateFiles({ channel: product.get('channel'), product: source })
      );
    },
  });
}

export function setGTINErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    oversetType: ATTRIBUTE.GTIN,
    type: ATTRIBUTE.GTIN,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.GTIN }) &&
        getGTINError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setShippingErrors({ product, shopData }) {
  const channel = product.get('channel');
  return updateErrors({
    errorsTypes: SHIPPING_ERRORS[channel],
    path: FORM.SHIPPING,
    product,
    validator(source) {
      switch (channel) {
        case EBAY: {
          return validateShipping({
            allowEmpty: !shouldValidate({ product, type: FORM.SHIPPING }),
            channel,
            shipping: source.get(FORM.SHIPPING),
            shopData,
          });
        }

        case ETSY: {
          if (source.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) return EMPTY.MAP;

          const baseAllowEmpty = !shouldValidate({ product, type: FORM.SHIPPING }) || undefined;
          const hasChannelId = product.get('channelProductId') !== null && product.get('channelProductId') !== undefined;
          const isPublished = hasChannelId && product.get('channelProductId') !== product.get('productId');
          const allowEmpty = isPublished ? true : baseAllowEmpty;

          return validateShipping({
            allowEmpty,
            channel,
            shipping: source.get(FORM.SHIPPING),
            shopData,
          });
        }

        case FAIRE: {
          return validateShipping({
            allowEmpty: !shouldValidate({ product, type: FORM.SHIPPING }),
            allowZero: (
              product.get('status') !== STATUS[FAIRE].PUBLISHED &&
              product.get('status') !== STATUS[FAIRE].UNPUBLISHED
            ),
            channel,
            shipping: source.get(FORM.SHIPPING),
            shopData,
          });
        }

        default: {
          return undefined;
        }
      }
    },
  });
}

export function setProductionPartnersErrors({ product }) {
  return updateError({
    path: FORM.DETAILS,
    product,
    type: ATTRIBUTE.PRODUCTION_PARTNERS,
    validator(productionPartners) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.PRODUCTION_PARTNERS }) &&
        getProductionPartnersError({
          channel: product.get('channel'),
          productionPartners,
          isSupply: product.getIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE]),
          whoMade: product.getIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE]),
          whenMade: product.getIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE]),
        })
      );
    },
  });
}

export function setPersonalizationErrors({ product: source, userFeatures }) {
  if (!userFeatures.get(FEATURE.ETSY_PERSONALIZATION_QUESTIONS)) {
    return updateErrors({
      errorsTypes: PERSONALIZATION_ERRORS,
      path: FORM.PERSONALIZATION,
      product: source,
      validator(product) {
        return shouldValidate({ product, type: ATTRIBUTE.PERSONALIZATION })
          ? validatePersonalization({
            channel: product.get('channel'),
            personalization: product.get(FORM.PERSONALIZATION),
            userFeatures,
          })
          : EMPTY.MAP;
      },
    });
  }

  function reducePersonalizationErrors(productErrors, questionErrors, type) {
    function reduceQuestionErrors(result, error, key) {
      return result.has(key)
        ? result
        : isImmutable(error)
          ? reduceErrors(error, false)
          : result.set(key, error);
    }

    if (isImmutable(questionErrors)) {
      return questionErrors.reduce(reduceQuestionErrors, productErrors);
    }

    if (typeof questionErrors === 'string') {
      return productErrors.set(type, questionErrors);
    }

    return productErrors;
  }

  let product = source;
  const errors = shouldValidate({ product, type: ATTRIBUTE.PERSONALIZATION })
    ? validatePersonalization({
      channel: product.get('channel'),
      personalization: product.get(FORM.PERSONALIZATION),
      userFeatures,
    })
    : EMPTY.MAP;

  if (getSize(errors) > 0) {
    product = product.setIn([FORM.PERSONALIZATION, ERROR], errors);

    if (product.has(ERROR)) {
      product = product.setIn([ERROR, FORM.PERSONALIZATION], errors.reduce(reducePersonalizationErrors, EMPTY.MAP));
    }
  } else {
    product = clearErrors({ errors: [FORM.PERSONALIZATION], product });
  }

  return product;
}

export function setTagsErrors({ product }) {
  return updateError({
    product,
    type: ATTRIBUTE.TAGS,
    validator(tags) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.TAGS }) &&
        getTagsError({ channel: product.get('channel'), tags })
      );
    },
  });
}

export function setMaterialsErrors({ product }) {
  return updateError({
    path: FORM.DETAILS,
    product,
    type: ATTRIBUTE.MATERIALS,
    validator(materials) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.MATERIALS }) &&
        getMaterialsError({ channel: product.get('channel'), materials })
      );
    },
  });
}

export function setPriceErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    type: ATTRIBUTE.PRICE,
    oversetType: ATTRIBUTE.PRICE,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.PRICE }) &&
        getPriceError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setRetailPriceErrors({ product, ...rest }) {
  return updateErrors({
    errorsTypes: [ATTRIBUTE.RETAIL_PRICE],
    path: FORM.INVENTORY,
    product,
    validator() {
      function updateRetailPriceErrors(errors, currency) {
        if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, OVERSET]) ||
          !shouldValidate({ currency, product, type: ATTRIBUTE.RETAIL_PRICE })
        ) {
          return errors.delete(currency);
        }

        const allowZero = !product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, REQUIRED]);
        const retail = product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, currency]);
        const wholesale = product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency]);
        const error = getRetailPriceError({ allowZero, currency, retail, wholesale });

        return error
          ? errors.set(currency, error)
          : errors.delete(currency);
      }

      const errors = rest.currency
        ? updateRetailPriceErrors(
          product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, ERROR], EMPTY.MAP),
          rest.currency,
        )
        : CURRENCIES[FAIRE].reduce(updateRetailPriceErrors, EMPTY.MAP);

      return getSize(errors) > 0
        ? Map({ [ATTRIBUTE.RETAIL_PRICE]: errors })
        : EMPTY.MAP;
    },
  });
}

export function setWholesalePriceErrors({ product, ...rest }) {
  return updateErrors({
    errorsTypes: [ATTRIBUTE.WHOLESALE_PRICE],
    path: FORM.INVENTORY,
    product,
    validator() {
      function updateWholesalePriceErrors(errors, currency) {
        if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, OVERSET]) ||
          !shouldValidate({ currency, product, type: ATTRIBUTE.WHOLESALE_PRICE })
        ) {
          return errors.delete(currency);
        }

        const allowZero = !product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, REQUIRED]);
        const retail = product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, VALUE, currency]);
        const wholesale = product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency]);
        const error = getWholesalePriceError({ allowZero, currency, retail, wholesale });

        return error
          ? errors.set(currency, error)
          : errors.delete(currency);
      }

      const errors = rest.currency
        ? updateWholesalePriceErrors(
          product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, ERROR], EMPTY.MAP),
          rest.currency,
        )
        : CURRENCIES[FAIRE].reduce(updateWholesalePriceErrors, EMPTY.MAP);

      return getSize(errors) > 0
        ? Map({ [ATTRIBUTE.WHOLESALE_PRICE]: errors })
        : EMPTY.MAP;
    },
  });
}

export function setQuantityErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    type: ATTRIBUTE.QUANTITY,
    oversetType: ATTRIBUTE.QUANTITY,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.QUANTITY }) &&
        (product.get('channel') !== SHOPIFY || product.getIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE])) &&
        getQuantityError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setSKUErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    type: ATTRIBUTE.SKU,
    oversetType: ATTRIBUTE.SKU,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.SKU }) &&
        getSKUError({
          allowEmpty: !product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, REQUIRED]),
          channel: product.get('channel'),
          value,
        })
      );
    },
  });
}

export function setBarcodeErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    type: ATTRIBUTE.BARCODE,
    oversetType: ATTRIBUTE.BARCODE,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.BARCODE }) &&
        getBarcodeError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setCAPErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    type: ATTRIBUTE.CAP,
    oversetType: ATTRIBUTE.CAP,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.CAP }) &&
        getCAPError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setCPIErrors({ product }) {
  return updateError({
    path: FORM.INVENTORY,
    product,
    type: ATTRIBUTE.CPI,
    oversetType: ATTRIBUTE.CPI,
    validator(value) {
      return (
        shouldValidate({ product, type: ATTRIBUTE.CPI }) &&
        getCPIError({ channel: product.get('channel'), value })
      );
    },
  });
}

export function setWeightErrors({ product }) {
  const channel = product.get('channel');

  switch (channel) {
    case FAIRE: {
      return updateError({
        path: FORM.INVENTORY,
        oversetType: ATTRIBUTE.WEIGHT,
        product,
        type: ATTRIBUTE.WEIGHT,
        validator(value) {
          return (
            shouldValidate({ product, type: ATTRIBUTE.WEIGHT }) &&
            getWeightError({ channel: product.get('channel'), value })
          );
        },
      });
    }

    case SHOPIFY: {
      return updateError({
        path: FORM.INVENTORY,
        oversetType: ATTRIBUTE.PHYSICAL,
        product,
        type: ATTRIBUTE.WEIGHT,
        validator(value) {
          return (
            shouldValidate({ product, type: ATTRIBUTE.WEIGHT }) &&
            product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE]) &&
            getWeightError({ channel: product.get('channel'), value })
          );
        },
      });
    }

    default: {
      return product;
    }
  }
}

export function setHeightErrors({ product }) {
  const channel = product.get('channel');

  switch (channel) {
    case FAIRE: {
      return updateError({
        path: FORM.INVENTORY,
        oversetType: ATTRIBUTE.HEIGHT,
        product,
        type: ATTRIBUTE.HEIGHT,
        validator(value) {
          return (
            shouldValidate({ product, type: ATTRIBUTE.HEIGHT }) &&
            getHeightError({ channel: product.get('channel'), value })
          );
        },
      });
    }

    default: {
      return product;
    }
  }
}

export function setLengthErrors({ product }) {
  const channel = product.get('channel');

  switch (channel) {
    case FAIRE: {
      return updateError({
        path: FORM.INVENTORY,
        oversetType: ATTRIBUTE.LENGTH,
        product,
        type: ATTRIBUTE.LENGTH,
        validator(value) {
          return (
            shouldValidate({ product, type: ATTRIBUTE.LENGTH }) &&
            getLengthError({ channel: product.get('channel'), value })
          );
        },
      });
    }

    default: {
      return product;
    }
  }
}

export function setWidthErrors({ product }) {
  const channel = product.get('channel');

  switch (channel) {
    case FAIRE: {
      return updateError({
        path: FORM.INVENTORY,
        oversetType: ATTRIBUTE.WIDTH,
        product,
        type: ATTRIBUTE.WIDTH,
        validator(value) {
          return (
            shouldValidate({ product, type: ATTRIBUTE.WIDTH }) &&
            getWidthError({ channel: product.get('channel'), value })
          );
        },
      });
    }

    default: {
      return product;
    }
  }
}

export function setTaxonomyAttributesErrors({ product }) {
  return updateErrors({
    errorsTypes: [ATTRIBUTE.TAXONOMY_ATTRIBUTES],
    path: FORM.DETAILS,
    product,
    validator(source) {
      return shouldValidate({ product, type: ATTRIBUTE.TAXONOMY_ATTRIBUTES })
        ? validateTaxonomyAttributes({ channel: product.get('channel'), product: source })
        : EMPTY.MAP;
    },
  });
}

export function setProcessingProfileErrors({ product, shopData }) {
  return updateError({
    path: FORM.SHIPPING,
    product,
    type: ATTRIBUTE.PROCESSING_PROFILE,
    oversetType: ATTRIBUTE.PROCESSING_PROFILE,
    validator(value) {
      return (
        getProcessingProfileError({
          allowEmpty: !shouldValidate({ product, type: ATTRIBUTE.PROCESSING_PROFILE }),
          shopData,
          value,
        })
      );
    },
  });
}

export function updateProductErrorsFromVariations(result, errors, type) {
  let product = result;
  const channel = product.get('channel');

  function reduceErrorsFor(name) {
    return function reduce(error, item) {
      return error || item.get(name);
    };
  }

  switch (type) {
    case VARIANTS: {
      const nameError = errors.reduce(reduceErrorsFor('name'), undefined);
      const optionsError = errors.reduce(reduceErrorsFor('options'), undefined);

      if (nameError) {
        product = product.setIn([ERROR, ATTRIBUTE.VARIATIONS, 'variantName'], nameError);
      } else {
        product = product.deleteIn([ERROR, ATTRIBUTE.VARIATIONS, 'variantName']);
      }

      if (optionsError) {
        product = product.setIn([ERROR, ATTRIBUTE.VARIATIONS, 'variantOptions'], optionsError);
      } else {
        product = product.deleteIn([ERROR, ATTRIBUTE.VARIATIONS, 'variantOptions']);
      }

      switch (channel) {
        case ETSY: {
          const scaleError = errors.reduce(reduceErrorsFor('scale'), undefined);

          if (scaleError) {
            product = product.setIn([ERROR, ATTRIBUTE.VARIATIONS, 'variantScale'], scaleError);
          } else {
            product = product.deleteIn([ERROR, ATTRIBUTE.VARIATIONS, 'variantScale']);
          }

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    default: {
      const error = reduceErrors(errors, false);

      if (error) {
        product = product.setIn([ERROR, ATTRIBUTE.VARIATIONS, type], error);
      } else {
        product = product.deleteIn([ERROR, ATTRIBUTE.VARIATIONS, type]);
      }

      break;
    }
  }

  if (product.hasIn([ERROR, ATTRIBUTE.VARIATIONS]) && getSize(product.getIn([ERROR, ATTRIBUTE.VARIATIONS])) === 0) {
    product = product.deleteIn([ERROR, ATTRIBUTE.VARIATIONS]);
  }

  return product;
}

export function updateProductVariationsErrors({ product: source, shopData }) {
  let product = source;
  const channel = product.get('channel');

  switch (channel) {
    case EBAY: {
      if (getSize(product.getIn([FORM.VARIATIONS, VARIANTS]))) {
        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET])) {
          product = setPriceErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.PRICE])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.PRICE], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET])) {
          product = setQuantityErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.QUANTITY])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.QUANTITY], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET])) {
          product = setSKUErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.SKU])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.SKU], path: FORM.INVENTORY, product });
        }
      } else {
        product = setPriceErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]) });
        product = setQuantityErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]) });
        product = setSKUErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]) });
      }

      break;
    }

    case ETSY: {
      const isDigital = product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
      const influence = isDigital ? EMPTY.MAP : product.getIn([FORM.VARIATIONS, INFLUENCE]);

      product = setPriceErrors({
        product: (
          product.getIn([FORM.VARIATIONS, RETAIN, ATTRIBUTE.PRICE]) ||
          getSize(influence.get(ATTRIBUTE.PRICE)) > 0
        )
          ? product.setIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET], true)
          : product.deleteIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]),
      });

      product = setQuantityErrors({
        product: (
          product.getIn([FORM.VARIATIONS, RETAIN, ATTRIBUTE.QUANTITY]) ||
          getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0
        )
          ? product.setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET], true)
          : product.deleteIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]),
      });

      product = setSKUErrors({
        product: (
          product.getIn([FORM.VARIATIONS, RETAIN, ATTRIBUTE.SKU]) ||
          getSize(influence.get(ATTRIBUTE.SKU)) > 0
        )
          ? product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET], true)
          : product.deleteIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]),
      });

      if (!isDigital) {
        product = setProcessingProfileErrors({
          product: (
            product.getIn([FORM.VARIATIONS, RETAIN, ATTRIBUTE.PROCESSING_PROFILE]) ||
            getSize(influence.get(ATTRIBUTE.PROCESSING_PROFILE)) > 0
          )
            ? product.setIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE, OVERSET], true)
            : product.deleteIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE, OVERSET]),
          shopData,
        });
      }

      break;
    }

    case FAIRE: {
      if (getSize(product.getIn([FORM.VARIATIONS, VARIANTS]))) {
        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.DEADLINE_DATE, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.DEADLINE_DATE, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.END_DATE, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.END_DATE, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.GTIN, OVERSET])) {
          product = setGTINErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.GTIN, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.GTIN, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.GTIN])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.GTIN], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, OVERSET])) {
          product = setHeightErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.HEIGHT])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.HEIGHT], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.LENGTH, OVERSET])) {
          product = setWidthErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.LENGTH, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.LENGTH, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.LENGTH])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.LENGTH], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.RESTOCK_DATE, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.RESTOCK_DATE, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, OVERSET])) {
          product = clearErrors({
            errors: [ATTRIBUTE.RETAIL_PRICE],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, OVERSET], true),
          });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.RETAIL_PRICE])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.RETAIL_PRICE], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.START_DATE, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.START_DATE, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.TARIFF_CODE, OVERSET])) {
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.TARIFF_CODE, OVERSET], true);
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, OVERSET])) {
          product = setWeightErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.WEIGHT])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.WEIGHT], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, OVERSET])) {
          product = clearErrors({
            errors: [ATTRIBUTE.WHOLESALE_PRICE],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, OVERSET], true),
          });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.WHOLESALE_PRICE])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.WHOLESALE_PRICE], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.WIDTH, OVERSET])) {
          product = setWidthErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.WIDTH, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.WIDTH, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.WIDTH])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.WIDTH], path: FORM.INVENTORY, product });
        }
      } else {
        product = product
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.DEADLINE_DATE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.END_DATE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.GTIN, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.LENGTH, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.RESTOCK_DATE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.START_DATE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.TARIFF_CODE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, OVERSET])
          .deleteIn([FORM.INVENTORY, ATTRIBUTE.WIDTH, OVERSET]);

        product = setGTINErrors({ product });
        product = setRetailPriceErrors({ product });
        product = setWholesalePriceErrors({ product });
        product = setWeightErrors({ product });
        product = setHeightErrors({ product });
        product = setLengthErrors({ product });
        product = setWidthErrors({ product });
      }

      break;
    }

    case SHOPIFY: {
      if (getSize(product.getIn([FORM.VARIATIONS, VARIANTS])) > 0) {
        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, OVERSET])) {
          product = setBarcodeErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.BARCODE])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.BARCODE], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.CAP, OVERSET])) {
          product = setCAPErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.CAP, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.CAP, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.CAP])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.CAP], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.CPI, OVERSET])) {
          product = setCPIErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.CPI, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.CPI, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.CPI])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.CPI], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET])) {
          product = setPriceErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.PRICE])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.PRICE], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET])) {
          product = setQuantityErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.QUANTITY])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.QUANTITY], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET])) {
          product = setSKUErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.SKU])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.SKU], path: FORM.INVENTORY, product });
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET])) {
          product = setWeightErrors({ product: product.setIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET], true) });
        } else if (
          product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, ERROR]) ||
          product.getIn([ERROR, ATTRIBUTE.WEIGHT])
        ) {
          product = clearErrors({ errors: [ATTRIBUTE.WEIGHT], path: FORM.INVENTORY, product });
        }
      } else {
        product = setBarcodeErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, OVERSET]) });
        product = setCAPErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.CAP, OVERSET]) });
        product = setCPIErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.CPI, OVERSET]) });
        product = setPriceErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]) });
        product = setQuantityErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]) });
        product = setSKUErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]) });
        product = setWeightErrors({ product: product.deleteIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]) });
      }

      break;
    }

    default: {
      break;
    }
  }

  return product.has(ERROR) && getSize(product.getIn([FORM.VARIATIONS, ERROR]))
    ? product.getIn([FORM.VARIATIONS, ERROR]).reduce(updateProductErrorsFromVariations, product)
    : product;
}

export function setVariationsErrors({ product: source, shop, shopData }) {
  let product = source;
  const channel = product.get('channel');

  switch (channel) {
    case EBAY: {
      const marketplaceId = shop.get('marketplaceId');
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
      const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);
      const variations = product.get(FORM.VARIATIONS);
      let variantsSize = getSize(variations.get(VARIANTS));
      product = updateProductVariationsErrors({
        product: product.set(FORM.VARIATIONS,
          updateVariationsErrors({ category, channel, shopData, itemSpecifics, shop, variations })
        ),
        shopData,
      });

      if (
        variantsSize > 0 &&
        get([EBAY, marketplaceId, 'CATEGORIES', category, 'features', 'variations'])(DATA) === false
      ) {
        product = updateProductVariationsErrors({
          product: product.setIn([FORM.VARIATIONS, ERROR, ATTRIBUTE.CATEGORY],
            PRODUCT_ERRORS.VARIATIONS.NOT_ALLOWED
          ),
          shopData,
        });
      } else {
        if (variations.getIn([ERROR, ATTRIBUTE.CATEGORY]) === PRODUCT_ERRORS.VARIATIONS.NOT_ALLOWED) {
          product = updateProductVariationsErrors({
            product: product.setIn([FORM.VARIATIONS, ERROR, ATTRIBUTE.CATEGORY], false),
            shopData,
          });
        }
      }

      if (variantsSize > 0) {
        const properties = get([EBAY, marketplaceId, 'CATEGORIES', category, 'properties'])(DATA) || {};

        if (getSize(properties) > 0) {
          let startId = -variantsSize;

          for (let variantIndex = 0; variantIndex < variantsSize; variantIndex += 1) {
            const variant = product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex]);

            if (!variant) continue;

            const variantName = variant.get('name');
            const property = properties[variantName];
            const affected = (
              property &&
              getSize(property.affecting) > 0 &&
              property.affecting.find(function findAffectedProperty(item) {
                return properties[item].variations;
              })
            );

            if (
              !property ||
              !affected ||
              getSize(product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, affected, 'value'])) > 0
            ) {
              if (product.hasIn([FORM.VARIATIONS, INFLUENCE, variantIndex, 'affecting'])) {
                product = updateProductVariationsErrors({
                  product: product.set(FORM.VARIATIONS,
                    updateVariations({
                      product,
                      shopData,
                      validateOptions: true,
                      variants: product.getIn([FORM.VARIATIONS, VARIANTS]),
                      variations: product
                        .get(FORM.VARIATIONS)
                        .deleteIn([INFLUENCE, variantIndex, 'affecting'])
                        .deleteIn([INFLUENCE, product.getIn([FORM.VARIATIONS, INFLUENCE, variantIndex, 'affecting'])]),
                    })
                  ),
                  shopData,
                });
              } else if (
                property &&
                affected &&
                !product.hasIn([FORM.VARIATIONS, INFLUENCE, variantIndex])
              ) {
                product = product.setIn([FORM.VARIATIONS, INFLUENCE, variantIndex],
                  Map({
                    values: Set(properties[variantName].options),
                    type: getPropertyType(properties[variantName]),
                  })
                );
              }

              if (product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, variantName])) {
                product = product.deleteIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, variantName]);
              }

              continue;
            }

            if (product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, variantName])) {
              product = product.deleteIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, variantName]);
            }

            if (product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, affected])) {
              product = product.deleteIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, affected]);
            }

            const affects = { affected, variantIndex, variantName };

            product = updateProductVariationsErrors({
              product: product.set(FORM.VARIATIONS,
                updateVariations({
                  product,
                  shopData,
                  validateOptions: true,
                  variants: product.getIn([FORM.VARIATIONS, VARIANTS]),
                  variations: updateInfluence({
                    affects,
                    category,
                    channel,
                    marketplaceId,
                    variations: product.get(FORM.VARIATIONS),
                  }),
                })
              ),
              shopData,
            });

            if (product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex + 1, 'name']) !== affected) {
              startId -= 1;
              const affectedVariant = Map({ id: startId, name: affected, options: EMPTY.LIST });

              const errors = product
                .getIn([FORM.VARIATIONS, ERROR, VARIANTS])
                .reduce(
                  function reduceVariantErrors(result, item, index) {
                    return index <= variantIndex
                      ? result.set(index, item)
                      : result.set(index + 1, item);
                  },
                  EMPTY.MAP.set(variantIndex + 1, Map({ options: VARIATIONS_ERRORS.OPTIONS.NO_OPTIONS }))
                );

              product = updateProductVariationsErrors({
                product: product.set(FORM.VARIATIONS,
                  updateVariations({
                    product,
                    shopData,
                    variants: product.getIn([FORM.VARIATIONS, VARIANTS]).splice(variantIndex + 1, 0, affectedVariant),
                    variations: product.get(FORM.VARIATIONS).setIn([ERROR, VARIANTS, variantIndex], errors),
                  })
                ),
                shopData,
              });

              variantIndex += 1;
              variantsSize += 1;
            }
          }
        }
      }

      return product;
    }

    case ETSY: {
      return updateProductVariationsErrors({
        product: product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])
          ? product.updateIn([FORM.VARIATIONS, ERROR], removeErrors)
          : product.set(FORM.VARIATIONS,
            updateVariationsErrors({ channel, shop, shopData, variations: product.get(FORM.VARIATIONS) })
          ),
        shopData,
      });
    }

    case FAIRE: {
      switch (product.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE])) {
        case SELLING_METHOD.SIZE: {
          if (!isOpenSizingSellingMethodAllowed(product.get(FORM.VARIATIONS))) {
            return setShippingErrors({
              product: updateProductVariationsErrors({
                product: product
                  .set(FORM.SHIPPING,
                    Map({
                      [ATTRIBUTE.CASE_SIZE]: makeValue(''),
                      [ATTRIBUTE.MINIMUM_ORDER_QUANTITY]: makeValue(
                        product.getIn([FORM.SHIPPING, ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE])
                      ),
                      [ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY]: makeValue(''),
                      [ATTRIBUTE.SELLING_METHOD]: makeValue(SELLING_METHOD.ITEM),
                    })
                  )
                  .set(FORM.VARIATIONS,
                    updateVariationsErrors({ channel, shop, shopData, variations: product.get(FORM.VARIATIONS) })
                  ),
              }),
              shopData,
            });
          }

          return setShippingErrors({
            product: updateProductVariationsErrors({
              product: product.set(FORM.VARIATIONS,
                updateVariationsErrors({ channel, shop, shopData, variations: product.get(FORM.VARIATIONS) })
              ),
              shopData,
            }),
          });
        }

        default: {
          return updateProductVariationsErrors({
            product: product.set(FORM.VARIATIONS,
              updateVariationsErrors({ channel, shop, shopData, variations: product.get(FORM.VARIATIONS) })
            ),
            shopData,
          });
        }
      }

    }

    default: {
      return updateProductVariationsErrors({
        product: product.set(FORM.VARIATIONS,
          updateVariationsErrors({ channel, shop, shopData, variations: product.get(FORM.VARIATIONS) })
        ),
        shopData,
      });
    }
  }
}

export function setProductErrors({ product: source, shop, shopData, userFeatures }) {
  let product = source.set(ERROR, EMPTY.MAP);
  const channel = product.get('channel');
  const errors = product.get(ERROR, EMPTY.MAP);

  switch (channel) {
    case EBAY: {
      product = setVariationsErrors({ product, shop, shopData });
      product = setShippingErrors({ product, shopData });
      product = setItemSpecificsErrors({ product, shop, shopData });
      product = setProductDetailsErrors({ product, shop });

      if (!errors.get(ATTRIBUTE.DESCRIPTION)) {
        product = setDescriptionErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.PHOTOS)) {
        product = setPhotosErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.PRICE)) {
        product = setPriceErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.QUANTITY)) {
        product = setQuantityErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.SKU)) {
        product = setSKUErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.TITLE)) {
        product = setTitleErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.ITEM_CATEGORY)) {
        product = setCategoryErrors({ product, shop });
      }

      if (!errors.get(ATTRIBUTE.CONDITION)) {
        product = setConditionErrors({ product, shop });
      }

      break;
    }

    case ETSY: {
      product = setVariationsErrors({ product, shop, shopData });

      if (product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) {
        if (!errors.get(ATTRIBUTE.FILES)) {
          product = setFilesErrors({ product });
        }
      } else {
        product = setShippingErrors({ product, shopData });
      }

      product = product
        .getIn([FORM.VARIATIONS, ERROR])
        .reduce(updateProductErrorsFromVariations, product);

      if (!errors.get(ATTRIBUTE.DESCRIPTION)) {
        product = setDescriptionErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.PHOTOS)) {
        product = setPhotosErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.PRICE)) {
        product = setPriceErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.QUANTITY)) {
        product = setQuantityErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.SKU)) {
        product = setSKUErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.TAGS)) {
        product = setTagsErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.TITLE)) {
        product = setTitleErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.WHO_MADE)) {
        product = setWhoMadeErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.IS_SUPPLY)) {
        product = setIsSupplyErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.WHEN_MADE)) {
        product = setWhenMadeErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.CATEGORY)) {
        product = setCategoryErrors({ product, shop });
      }

      if (!errors.get(ATTRIBUTE.MATERIALS)) {
        product = setMaterialsErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.PRODUCTION_PARTNERS)) {
        product = setProductionPartnersErrors({ product });
      }

      product = setPersonalizationErrors({ product, userFeatures });
      product = setTaxonomyAttributesErrors({ product });

      break;
    }

    case FAIRE: {
      product = setVariationsErrors({ product, shop, shopData });
      product = setPhotosErrors({ product });
      product = setTitleErrors({ product });
      product = setDescriptionErrors({ product });
      product = setShippingErrors({ product });
      break;
    }

    case SHOPIFY: {
      product = setVariationsErrors({ product, shop, shopData });

      if (!errors.get(ATTRIBUTE.PHOTOS)) {
        product = setPhotosErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.SKU)) {
        product = setSKUErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.TAGS)) {
        product = setTagsErrors({ product });
      }

      if (!errors.get(ATTRIBUTE.TITLE)) {
        product = setTitleErrors({ product });
      }

      break;
    }

    default: {
      break;
    }
  }

  return setIsComplete(product);
}
